import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Seo from '~components/Seo'
import DataViewer from '~utils/DataViewer'
import resolveLink from '~utils/resolveLink'
import useBreakpoint from '~utils/useBreakpoint'
import { motion, useAnimation } from 'framer-motion'
import FeaturedArticle from '../components/FeaturedArticle'
import Section from '../components/Section'
import SlideDown from 'react-slidedown'
import useWindow from '~utils/useWindow'
import TileArticle from '~components/TileArticle'
import FadeTransition from '~components/FadeTransition'
import { 
	mobile,
	NavBar,
	NavButton,
	NavHeading,
	TopicsIcon,
	NavLinks,
	NavLink,
	OrderBy,
	SortDropDown,
	Sort,
	SortButton,
	MobileDropDown,
	MobileNav,
	DownArrow,
} from '../styles/global'

const Journal = ({ data, pageContext }) => {
	const { title, content } = data.page
	const controls = useAnimation()
	const [navOpen, setNavOpen] = useState(false)
	const [sortOpen, setSortOpen] = useState(false)
	const [articles, setArticles] = useState(data.allArticles ?? data.articles)

	const sorts = ['Featured', 'Newest first', 'Oldest first']
	const [currentSort, setCurrentSort] = useState(sorts[0])
	
	const window = useWindow()

	const { isMobile } = useBreakpoint()

	const isCurrent = page => 
		resolveLink(page).replace(/\/$/, '') === window?.location?.pathname?.replace(/\/$/, '')


	const toPage = page => {
		setNavOpen(false)
		if(isCurrent(page)) return null
		controls.start('hidden')
		if (window) {
			window.setTimeout(() => navigate(resolveLink(page), { state: { disableScrollUpdate: true }}), 600)
		}
	}

	useEffect(() => {
		controls.start('visible')
	}, [])

	useEffect(() => {
		setSortOpen(false)
		if(currentSort === 'Featured'){
			setArticles(data.allArticles ?? data.articles)
		} else if (articles?.nodes?.length) {
			let as = [...articles?.nodes]
			if(currentSort === 'Newest first') as.sort((a,b) => a.unixDate - b.unixDate)
			if(currentSort === 'Oldest first') as.sort((a,b) => b.unixDate - a.unixDate)
			setArticles({nodes: as})
		}
	}, [currentSort])

	const toggleNavSort = ()=> {
		if (navOpen){
			setNavOpen(false)
			setSortOpen(!sortOpen)
		} else {
			setSortOpen(!sortOpen)
		}
	}
	const toggleSortNav = ()=> {
		if (sortOpen){
			setSortOpen(false)
			setNavOpen(!navOpen)
		} else {
			setNavOpen(!navOpen)
		}
	}

	return(
		<>
			<Seo 
				title={title}
				metaTitle={content?.seo?.metaTitle}
				description={content?.seo?.metaDescription}
				image={content?.seo?.socialImage}
			/>
			<Headline>{content?.headline}</Headline>
			<NavBar>
				<NavHeading>
					{isMobile ? 
						<NavButton className='h3' onClick={()=> toggleSortNav()}>
							Topics
							<TopicsIcon css={css`transform: ${ navOpen ? 'rotate(45deg)': 'rotate(0deg)'};`}/>
						</NavButton>
						:
						<>
					Topic:
						</>
					}
				</NavHeading>
				<NavLinks>
					{!isMobile &&
						<>
							<NavLink 
								onClick={() => toPage({ _type: 'journalPage'})} 
								css={css`
									text-decoration: ${isCurrent({_type: 'journalPage'}) ? 'underline' : 'none' };
								`}
							>
								<h3>All</h3>
							</NavLink>
							{content.filterTopics?.map(topic => (
								<NavLink 
									onClick={() => toPage(topic)} 
									key={topic.slug.current}
									css={css`
										text-decoration: ${isCurrent(topic) ? 'underline' : 'none' };
								`}
								>
									<h3>{topic.title}</h3>
								</NavLink>
							))}
						</>
					}
				</NavLinks>
				<OrderBy className='h3' onClick={()=> toggleNavSort()}>
					Order By <DownArrow css={css`transform: ${ sortOpen ? 'rotate(180deg)': 'rotate(0deg)'};`}/>
				</OrderBy>
				{!isMobile &&
					<SortDropDown css={css`
					`}>
						<SlideDown>
							{sortOpen &&
								<Sort>
									{sorts.map(sort => (
										<SortButton className='h3' key={sort} onClick={() => setCurrentSort(sort)} css={css`
											text-decoration: ${sort === currentSort ? 'underline' : 'none' };
										`}>
											{sort}
										</SortButton>
									))}
								</Sort>
							}
						</SlideDown>
					</SortDropDown>
				}
			</NavBar>

			{isMobile &&
			<div css={css`position: relative;`}>
				<MobileDropDown css={css`
					border-top: ${navOpen || sortOpen ? '1px solid var(--black)' : '1px solid var(--beige)'};
					transition-delay: ${navOpen || sortOpen ? '0s' : '0.46s'};
				`}>
					<SlideDown>
						{navOpen &&
							<MobileNav>
								<NavLink 
									onClick={() => toPage({ _type: 'journalPage'})} 
									css={css`
									text-decoration: ${isCurrent({_type: 'journalPage'}) ? 'underline' : 'none' };
								`}
								>
									<h3>All</h3>
								</NavLink>
								{content.filterTopics?.map(topic => (
									<NavLink 
										onClick={() => toPage(topic)} 
										key={topic.slug.current}
										css={css`
										text-decoration: ${isCurrent(topic) ? 'underline' : 'none' };
								`}
									>
										<h3>{topic.title}</h3>
									</NavLink>
								))}
							</MobileNav>
						}
						{sortOpen &&
							<Sort>
								{sorts.map(sort => (
									<SortButton className='h3' key={sort} onClick={() => setCurrentSort(sort)} css={css`
									text-decoration: ${sort === currentSort ? 'underline' : 'none' };
								`}>
										{sort}
									</SortButton>
								))}
							</Sort>
						}
					</SlideDown>
				</MobileDropDown>
			</div>
			}
			<div key={pageContext.slug}>
				<motion.div 
					animate={controls}
					initial='hidden'
					transition={{ duration: 0.6 }}
					variants={{
						visible: { opacity: 1 },
						hidden: { opacity: 0 }
					}}
				>
					{articles?.nodes?.length > 0 ? 
						<FadeTransition integer={currentSort}>
							<ArticleGrid>
								{articles.nodes.map((article, i) => 
									( 
										i % 4 === 4 - 1 ? 
											<FeatureArticle content={article} /> :
											<ArticleTile key={article.slug?.current} article={article}/>
									)
								)}
							</ArticleGrid>
						</FadeTransition>
						
						:
						<Section>
							<Message>
								<h2>No Articles Found</h2>
							</Message>
						</Section>
					}
				</motion.div>
			</div>
			<DataViewer data={data} name="data"/>
		</>
	)
}

const Headline = styled.h1`
	padding: var(--xxxl) var(--xl);
	border-bottom: 1px solid var(--black);
	text-align: center;
`

const FeatureArticle = styled(FeaturedArticle)`
	border-bottom: 1px solid var(--black);
	grid-column: span 3;
`
const ArticleGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
`

const ArticleTile = styled(TileArticle)`
	border-bottom: 1px solid var(--black);
	border-right: 1px solid var(--black);
	display: block;
	grid-column: span 1;
	&:nth-child(4n+3){
		border-right: none;
	}
	${mobile}{
		border-right: none;
		grid-column: span 3;
	}
`
const Message = styled.div`
	grid-column: span 12;
	padding: var(--xxxl) 0;
	text-align: center;
	${mobile}{
		grid-column: span 6;
	}
`

Journal.propTypes = {
	data: PropTypes.object,
	pageContext: PropTypes.object,
}

export const query = graphql`
	query JournalQuery($slug: String, $isIndex: Boolean!) {
		page: sanityJournalPage {
			title
			content {
				headline
				filterTopics {
					title
					_type
					slug {
						current
					}
				}
			}
		}
		allArticles: allSanityArticle(sort: { order: [ASC, DESC], fields: [featured, datePublished] }) @include(if: $isIndex) {
			nodes {
				...articleThumb
			}
		}
		articles: allSanityArticle(
			sort: { order: [ASC, DESC], fields: [featured, datePublished] }
			filter: {content: {topics: {elemMatch: {slug: {current: {eq: $slug}}}}}}
			) 
			@skip(if: $isIndex) {
			nodes {
				...articleThumb
			}
		}
	}

`

export default Journal
